/**
 * THE COMPOUNDING LIFE — site behaviour (DEV).
 * Small, dependency-free: spread reveals, product gallery, variant + quantity,
 * and the mobile purchase bar on the book page. Cart is handled by tcl-home.js
 * (native Shopify cart + Horizon drawer).
 * v15: also loads tcl-cart-bump.js on every page, so the free-workbook gift and the
 * cart add-on tick box work for first-time visitors (they used to load only when the
 * cart already held the mask when the page opened).
 */
import('./tcl-cart-bump.js').catch(() => {});

const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

function initIn(scope = document) {
  const els = scope.querySelectorAll('[data-tcl-in]');
  if (!els.length) return;
  if (reduce.matches || !('IntersectionObserver' in window)) {
    els.forEach((el) => el.classList.add('is-in'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      }
    },
    { threshold: 0.15 }
  );
  els.forEach((el) => io.observe(el));
}

function initPdp(scope = document) {
  scope.querySelectorAll('[data-tcl-pdp]').forEach((root) => {
    if (root.dataset.bound) return;
    root.dataset.bound = 'true';

    const stage = root.querySelector('[data-tcl-stage]');
    const imgs = stage ? [...stage.querySelectorAll('img')] : [];
    const thumbs = [...root.querySelectorAll('[data-tcl-thumb]')];
    const show = (i) => {
      if (!imgs[i]) return;
      imgs.forEach((im, k) => im.classList.toggle('is-active', k === i));
      thumbs.forEach((t, k) => t.setAttribute('aria-current', String(k === i)));
    };
    thumbs.forEach((t) => t.addEventListener('click', () => show(Number(t.dataset.tclThumb))));

    root.querySelectorAll('[data-tcl-qty]').forEach((q) => {
      const input = q.querySelector('input');
      q.querySelectorAll('[data-step]').forEach((b) =>
        b.addEventListener('click', () => {
          const n = Math.max(1, (parseInt(input.value, 10) || 1) + Number(b.dataset.step));
          input.value = String(n);
        })
      );
      input.addEventListener('change', () => {
        input.value = String(Math.max(1, parseInt(input.value, 10) || 1));
      });
    });

    root.querySelectorAll('[data-tcl-variant]').forEach((sel) => {
      const form = sel.closest('form');
      const price = root.querySelector('[data-tcl-price]');
      const add = form?.querySelector('[data-tcl-add]');
      const label = add?.querySelector('.tcl-btn__label');
      const sync = () => {
        const o = sel.selectedOptions[0];
        if (!o) return;
        if (price && o.dataset.price) price.textContent = o.dataset.price;
        const ok = o.dataset.available === 'true';
        if (add) add.disabled = !ok;
        if (label) label.textContent = ok ? 'Add to cart' : 'Sold out';
        if (o.dataset.media) {
          const i = imgs.findIndex((im) => im.dataset.mediaId === o.dataset.media);
          if (i > -1) show(i);
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', o.value);
        window.history.replaceState({}, '', url);
      };
      sel.addEventListener('change', sync);
    });

    const bar = root.querySelector('[data-tcl-bar]');
    const buy = root.querySelector('.tcl-bpdp__hero .tcl-bpdp__buy');
    const final = root.querySelector('.tcl-bpdp__final');
    if (bar && buy && 'IntersectionObserver' in window) {
      const vis = new Map();
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => vis.set(e.target, e.isIntersecting));
        const on = !vis.get(buy) && !vis.get(final);
        bar.classList.toggle('is-on', on);
        bar.setAttribute('aria-hidden', String(!on));
      });
      io.observe(buy);
      if (final) io.observe(final);
    }
  });
}

function boot(scope) {
  initIn(scope);
  initPdp(scope);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
} else {
  boot(document);
}
document.addEventListener('shopify:section:load', (e) => boot(e.target));
